import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { FileText, Eye, Check, X, Filter, Calendar, User, Clock, AlertTriangle } from "lucide-react";

interface RemoteWorkRequest {
  id: string;
  destination_country: string;
  start_date: string;
  end_date: string;
  requested_points: number;
  manager_approval_status: string;
  hr_approval_status: string;
  risk_level: string;
  risk_notes: string | null;
  comment: string | null;
  created_at: string;
  employee: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    position: string | null;
  };
}

interface ManagerRequestsTabProps {
  managerProfileId: string | null;
}

const ManagerRequestsTab = ({ managerProfileId }: ManagerRequestsTabProps) => {
  const [requests, setRequests] = useState<RemoteWorkRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<RemoteWorkRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [selectedRequest, setSelectedRequest] = useState<RemoteWorkRequest | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (managerProfileId) {
      fetchRequests();
    }
  }, [managerProfileId]);

  useEffect(() => {
    let filtered = requests;
    
    if (statusFilter !== "all") {
      filtered = requests.filter(request => request.manager_approval_status === statusFilter);
    }
    
    setFilteredRequests(filtered);
  }, [requests, statusFilter]);

  const fetchRequests = async () => {
    if (!managerProfileId) return;

    try {
      // Get employees managed by this manager
      const { data: employees, error: empError } = await supabase
        .from('employees')
        .select('id, first_name, last_name, email, position')
        .eq('manager_profile_id', managerProfileId);

      if (empError) throw empError;

      if (!employees || employees.length === 0) {
        setRequests([]);
        setLoading(false);
        return;
      }

      const employeeIds = employees.map(e => e.id);

      // Get requests for those employees
      const { data: requestsData, error: reqError } = await supabase
        .from('remote_work_requests')
        .select('*')
        .in('employee_id', employeeIds)
        .order('created_at', { ascending: false });

      if (reqError) throw reqError;

      // Map employee info to requests
      const requestsWithEmployees = (requestsData || []).map(req => {
        const emp = employees.find(e => e.id === req.employee_id);
        return {
          ...req,
          employee: emp || {
            id: req.employee_id,
            first_name: 'N/A',
            last_name: 'N/A',
            email: 'N/A',
            position: null
          }
        };
      });

      setRequests(requestsWithEmployees);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de charger les demandes"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId: string) => {
    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const { error } = await supabase
        .from('remote_work_requests')
        .update({
          manager_approval_status: 'approved',
          manager_approved_at: new Date().toISOString(),
          manager_approver_id: managerProfileId
        })
        .eq('id', requestId);

      if (error) throw error;

      toast({
        title: "Demande approuvée",
        description: "La demande a été transmise aux RH pour validation finale"
      });

      setIsDetailDialogOpen(false);
      fetchRequests();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Impossible d'approuver la demande"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async (requestId: string) => {
    if (!rejectionReason.trim()) {
      toast({
        variant: "destructive",
        title: "Motif requis",
        description: "Veuillez indiquer un motif de refus"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('remote_work_requests')
        .update({
          manager_approval_status: 'rejected',
          manager_approval_reason: rejectionReason,
          manager_approved_at: new Date().toISOString(),
          manager_approver_id: managerProfileId,
          status: 'rejected'
        })
        .eq('id', requestId);

      if (error) throw error;

      toast({
        title: "Demande refusée",
        description: "L'employé a été notifié du refus"
      });

      setIsDetailDialogOpen(false);
      setRejectionReason("");
      fetchRequests();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Impossible de refuser la demande"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getManagerStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />En attente</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-800">✓ Approuvée</Badge>;
      case 'rejected':
        return <Badge variant="destructive">✗ Refusée</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getRiskBadge = (level: string) => {
    switch (level) {
      case 'low':
        return <Badge variant="outline" className="text-green-600 border-green-200">Risque faible</Badge>;
      case 'medium':
        return <Badge variant="outline" className="text-yellow-600 border-yellow-200"><AlertTriangle className="w-3 h-3 mr-1" />Risque moyen</Badge>;
      case 'high':
        return <Badge variant="outline" className="text-red-600 border-red-200"><AlertTriangle className="w-3 h-3 mr-1" />Risque élevé</Badge>;
      default:
        return null;
    }
  };

  const calculateDays = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const openRequestDetail = (request: RemoteWorkRequest) => {
    setSelectedRequest(request);
    setRejectionReason("");
    setIsDetailDialogOpen(true);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-1/4"></div>
            <div className="h-64 bg-muted rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Demandes à valider
              </CardTitle>
              <CardDescription>
                {requests.filter(r => r.manager_approval_status === 'pending').length} demande(s) en attente de votre validation
              </CardDescription>
            </div>
            
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="approved">Approuvées</SelectItem>
                  <SelectItem value="rejected">Refusées</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {filteredRequests.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {requests.length === 0 ? "Aucune demande de votre équipe" : "Aucune demande ne correspond aux filtres"}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredRequests.map((request) => (
                <div 
                  key={request.id} 
                  className={`p-4 md:p-6 border rounded-lg transition-all hover:shadow-md ${
                    request.manager_approval_status === 'pending' ? 'border-yellow-200 bg-yellow-50/50' :
                    request.manager_approval_status === 'approved' ? 'border-green-200 bg-green-50/50' :
                    'border-red-200 bg-red-50/50'
                  }`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1 space-y-3">
                      {/* Employee & Status Row */}
                      <div className="flex flex-wrap items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="font-semibold">
                          {request.employee.first_name} {request.employee.last_name}
                        </span>
                        <span className="text-muted-foreground">•</span>
                        <span className="text-sm text-muted-foreground">
                          {request.employee.position || 'Poste non défini'}
                        </span>
                        {getManagerStatusBadge(request.manager_approval_status)}
                      </div>

                      {/* Destination & Dates */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                        <div>
                          <span className="text-muted-foreground">Destination:</span>
                          <p className="font-medium">{request.destination_country}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Période:</span>
                          <p className="font-medium">
                            {new Date(request.start_date).toLocaleDateString('fr-FR')} - {new Date(request.end_date).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Durée:</span>
                          <p className="font-medium">{calculateDays(request.start_date, request.end_date)} jours</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Points:</span>
                          <p className="font-medium">{request.requested_points} 🪺</p>
                        </div>
                      </div>

                      {/* Risk Assessment */}
                      {request.risk_level && (
                        <div className="flex items-center gap-2">
                          {getRiskBadge(request.risk_level)}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openRequestDetail(request)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Détails
                      </Button>
                      
                      {request.manager_approval_status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleApprove(request.id)}
                            disabled={isSubmitting}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Approuver
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => openRequestDetail(request)}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Refuser
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Request Detail Dialog */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Détails de la demande
            </DialogTitle>
            <DialogDescription>
              Demande de {selectedRequest?.employee.first_name} {selectedRequest?.employee.last_name}
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-6">
              {/* Employee Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Employé</Label>
                  <p className="font-medium">{selectedRequest.employee.first_name} {selectedRequest.employee.last_name}</p>
                  <p className="text-sm text-muted-foreground">{selectedRequest.employee.email}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Poste</Label>
                  <p className="font-medium">{selectedRequest.employee.position || 'Non spécifié'}</p>
                </div>
              </div>

              {/* Request Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Pays de destination</Label>
                  <p className="font-medium">{selectedRequest.destination_country}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Points demandés</Label>
                  <p className="font-medium">{selectedRequest.requested_points} points 🪺</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Date de début</Label>
                  <p className="font-medium">{new Date(selectedRequest.start_date).toLocaleDateString('fr-FR')}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Date de fin</Label>
                  <p className="font-medium">{new Date(selectedRequest.end_date).toLocaleDateString('fr-FR')}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Durée totale</Label>
                  <p className="font-medium">{calculateDays(selectedRequest.start_date, selectedRequest.end_date)} jours</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Demandé le</Label>
                  <p className="font-medium">{new Date(selectedRequest.created_at).toLocaleDateString('fr-FR')}</p>
                </div>
              </div>

              {/* Risk Assessment */}
              <div className="p-4 border rounded-lg">
                <Label className="text-sm font-medium text-muted-foreground">Évaluation des risques</Label>
                <div className="mt-2">
                  {getRiskBadge(selectedRequest.risk_level || 'low')}
                  {selectedRequest.risk_notes && (
                    <p className="text-sm mt-2 text-muted-foreground">{selectedRequest.risk_notes}</p>
                  )}
                </div>
              </div>

              {/* Employee Comment */}
              {selectedRequest.comment && (
                <div className="p-4 bg-muted/50 rounded-lg">
                  <Label className="text-sm font-medium text-muted-foreground">Commentaire de l'employé</Label>
                  <p className="mt-1">{selectedRequest.comment}</p>
                </div>
              )}

              {/* Rejection Reason Input */}
              {selectedRequest.manager_approval_status === 'pending' && (
                <div>
                  <Label htmlFor="rejectionReason">Motif de refus (obligatoire pour refuser)</Label>
                  <Textarea
                    id="rejectionReason"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Indiquez le motif du refus..."
                    className="mt-1"
                    rows={3}
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {selectedRequest?.manager_approval_status === 'pending' && (
              <>
                <Button
                  variant="destructive"
                  onClick={() => selectedRequest && handleReject(selectedRequest.id)}
                  disabled={isSubmitting || !rejectionReason.trim()}
                >
                  <X className="h-4 w-4 mr-2" />
                  Refuser
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => selectedRequest && handleApprove(selectedRequest.id)}
                  disabled={isSubmitting}
                >
                  <Check className="h-4 w-4 mr-2" />
                  Approuver
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setIsDetailDialogOpen(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ManagerRequestsTab;